




























































import {
  AdgroupFormData,
  FileUploadStatus,
  FullNormalFormData,
  SupportFileType,
  VideoBasicInfo,
  VideoVerifyMode,
  InventoryFlow,
  MaterialType,
} from '@tt4b/ttam-enums';
import Vue from 'vue';
import { getVideoFileInfo, queryToJson, logger, trackEvent } from '@tt4b/ttam-utils';
import { useMaterialVerify, useVideoShouldCut, useMaterialNameValidate } from '@tt4b/ttam-hooks';
import { computed, defineComponent, ref, PropType, toRefs, nextTick } from '@vue/composition-api';

import TtamMaterialUpload from './index';
import tiktokIcon from '@tt4b/ttam-assets/src/images/placement/placement-tt.png';
import topbuzzIcon from '@tt4b/ttam-assets/src/images/placement/placement-tb.png';
import pangleIcon from '@tt4b/ttam-assets/src/images/placement/placement-new-pangle.png';

const name = 'ttam-material-upload-compose';
const AppIconMap = {
  [InventoryFlow.TIKTOK]: tiktokIcon,
  [InventoryFlow.TOPBUZZ]: topbuzzIcon,
  [InventoryFlow.TIKTOKADNETWORK]: pangleIcon,
};

export default defineComponent({
  name,

  components: {
    TtamMaterialUploadTable: TtamMaterialUpload.TtamMaterialUploadTable,
    TtamMaterialUploadRequirement: TtamMaterialUpload.TtamMaterialUploadRequirement,
    TtamMaterialUploadSelectFileButton: TtamMaterialUpload.TtamMaterialUploadSelectFileButton,
  },

  props: {
    width: { type: String, default: '1080px' },
    topHeight: { type: Number },
    showMask: { type: Boolean, default: false },
    showClose: { type: Boolean, default: true },
    limit: { type: Number, default: 20 },
    adgroupData: { type: Object as PropType<AdgroupFormData>, default: () => ({}) },
    creativeData: { type: Object as PropType<FullNormalFormData>, default: () => ({}) },
  },

  setup(props, { emit }) {
    const { aadvid } = queryToJson();
    const { adgroupData, creativeData, limit } = toRefs(props);
    const files = ref<any[]>([]);
    const fileAccept = ref(SupportFileType.video.join(','));

    const slipDirection = ref(document.dir === 'rtl' ? 'slide-left' : 'slide-right');

    const isSlipOpen = ref(false);
    const videoSelectFileNode = ref();
    const open = () => {
      files.value = [];
      logger.info('videoSelectFileNode is preparing to clearFiles, then upload drawer will be open');
      videoSelectFileNode.value?.clearFiles();
      nextTick(() => {
        isSlipOpen.value = true;
        logger.info('The upload drawer has been opened');
      });
    };
    const close = () => {
      isSlipOpen.value = false;
      emit('close');
    };

    const isSelectedFiles = computed(() => files.value.length > 0);

    const isUploadNewButtonVisible = computed(() => isSelectedFiles.value && limit.value > 1);

    const isSelectFileButtonVisible = computed(() => !isSelectedFiles.value || isUploadNewButtonVisible.value);
    const _topHeight = computed(() => props.topHeight ?? Vue.prototype.$TTAMUI?.headerHeight);

    const { singleVideoVerify } = useMaterialVerify({
      adgroupFormData: adgroupData,
      creativeFormData: creativeData,
    });

    function getVerifyResultAndShouldCut(fileInfo: VideoBasicInfo) {
      if (fileInfo.verifyMode === VideoVerifyMode.after) {
        return { result: undefined, shouldCut: true };
      }
      const result = singleVideoVerify(fileInfo);
      result.forEach(item => (item.icon = AppIconMap[item.key]));
      let shouldCut = false;
      let totalStatus = result.some(item => item.status);
      if (!totalStatus) {
        shouldCut = useVideoShouldCut({
          fileType: fileInfo.raw.type,
          verifyResultCodes: result.map(item => item.verifyCode),
        });
        totalStatus = shouldCut;
      }

      return { result, shouldCut, totalStatus };
    }
    // 如果放在onSelectFiles里面,不方便写单测，所以提取出来
    const reportVideoUploadInfo = (
      placementValidateResult: { message: []; label: string }[],
      status: FileUploadStatus,
      shouldCut: boolean,
    ) => {
      trackEvent('video_upload_platform_validation_problem', {
        status: status === FileUploadStatus.VerifyPass ? 1 : 2,
        issues: (() => {
          if (status === FileUploadStatus.NameLengthInvalid) {
            return 'module_library_filename_length_hint';
          }
          if (status === FileUploadStatus.NameIncludeEmoji) {
            return 'module_library_filename_emoji_hint';
          }
          if (status === FileUploadStatus.NameDuplicate) {
            return 'module_library_filename_duplicate';
          }
          if (shouldCut) {
            return 'ad_creative_could_be_cutoff';
          }

          let allResult: string[] = [];
          placementValidateResult.forEach(inventory => {
            inventory.message.forEach(msg => allResult.push(`${inventory.label}:${msg}`));
          });

          return allResult.join(';');
        })(),
      });
    };

    /** 选择文件 */
    function onSelectFiles(filesSelect: (File & { uid: string })[] = []) {
      const filesArr = [...filesSelect];
      // filesSelect.forEach(item => filesArr.push(item));

      Promise.resolve()
        .then(() =>
          filesArr.map(item => ({
            uid: item.uid,
            name: item.name,
            size: item.size,
            raw: item as File,
            status: FileUploadStatus.Ready,
            percentage: 0,
          })),
        )
        .then(newFiles => {
          const promises = newFiles.map(item => getVideoFileInfo(item));
          return Promise.all(promises);
        })
        .then(newFiles =>
          newFiles.map(item => {
            const isAfterVerify = item.verifyMode === VideoVerifyMode.after;
            const { result, shouldCut, totalStatus: hasAvailablePlacement } = getVerifyResultAndShouldCut(item);
            const status =
              hasAvailablePlacement || isAfterVerify ? FileUploadStatus.VerifyPass : FileUploadStatus.VerifyFailed;
            return {
              ...item,
              status,
              shouldCut,
              percentage: 0,
              nameValidate: undefined,
              disabled: !hasAvailablePlacement,
              placementValidateResult: result,
            };
          }),
        )
        .then(newFiles => {
          const nameValidatePromises = newFiles.map(item =>
            useMaterialNameValidate({ targetName: item.name, type: MaterialType.Video }),
          );
          return Promise.all(nameValidatePromises).then(resArr => {
            resArr.forEach((err, index) => (newFiles[index].nameValidate = err.type));
            return newFiles;
          });
        })
        .then(newFiles => {
          newFiles.forEach(item => {
            const { placementValidateResult, status, shouldCut } = item;
            // 新增上传视频的埋点
            reportVideoUploadInfo(placementValidateResult, status, shouldCut);
          });
          files.value = [...files.value, ...newFiles];
        });
    }

    function onComplete(completedFiles) {
      logger.info('material upload compose', completedFiles);
      emit('complete', completedFiles);
      // files.value = [];
    }

    function onStartUpload(filesRef) {
      emit('start-upload', filesRef.value);
    }

    return {
      isSlipOpen,
      isSelectedFiles,
      isSelectFileButtonVisible,
      isUploadNewButtonVisible,
      videoSelectFileNode,

      files,
      aadvid,
      fileAccept,
      slipDirection,
      _topHeight,

      open,
      close,
      onComplete,
      onSelectFiles,
      onStartUpload,
      reportVideoUploadInfo,
    };
  },
});
